/**
 * From a scanned label to something the rest of the app already understands.
 *
 * THE KEY DECISION
 * ----------------
 * A scanned item is turned into a `Product` — the same shape the catalogue
 * uses. Not a parallel type with its own metric functions, because that would
 * mean two implementations of "protein per dollar" that could disagree, and the
 * one on the scanned item would be the untested one.
 *
 * Converting instead means `computeMetrics`, the comparison table, the metric
 * formatting and the ranking all work on scanned items unchanged, and any fix
 * to the engine reaches both at once. It also means the scanned item is ranked
 * by *exactly* the arithmetic the catalogue was ranked by, which is the whole
 * point of showing where it would place.
 *
 * Everything here is pure and synchronous. No network, no storage, no database.
 */

import type {
  DataOrigin,
  NutritionFacts,
  Nutrients,
  Product,
  Quantity,
} from '@/types/product';
import type { NutritionLabel } from './types';
import { CONFIDENCE_FLOOR } from './validate';

/** What the user typed alongside the scan. */
export interface ScanEntry {
  /** What they paid, in the catalogue's currency. */
  price: number | null;
  currency: string;
  /** Net contents of the pack, as sold. */
  packAmount: number | null;
  packUnit: 'g' | 'ml';
  /** Optional, purely so the user can tell two scans apart. */
  nickname: string | null;
}

/** A scanned item, alive only for as long as the tab is open. */
export interface ScannedItem {
  /** Session-local, never a catalogue id. Prefixed so it cannot collide. */
  id: string;
  label: NutritionLabel;
  entry: ScanEntry;
  /** Rebuilt whenever the label or the entry changes. */
  product: Product;
  scannedAt: string;
}

const SCAN_PROVIDER = 'scan';

/** The four the comparison actually needs; used for completeness reporting. */
const MACRO_FIELDS = [
  { key: 'calories' as const, label: 'Calories' },
  { key: 'protein' as const, label: 'Protein' },
  { key: 'totalCarbohydrate' as const, label: 'Carbohydrate' },
  { key: 'totalFat' as const, label: 'Fat' },
];

/**
 * Only values we actually trust become numbers.
 *
 * A low-confidence reading is dropped rather than passed through, because once
 * it reaches `computeMetrics` it is indistinguishable from a value read
 * cleanly — the uncertainty does not survive the conversion, so it has to be
 * acted on here. The review screen is where the user rescues these by typing
 * them in, which marks them `user` and lets them through.
 */
function trusted(label: NutritionLabel, key: keyof NutritionLabel['nutrients']): number | null {
  const n = label.nutrients[key];
  if (n === undefined || n.value === null) return null;
  if (n.source === 'user') return n.value;
  return n.confidence >= CONFIDENCE_FLOOR ? n.value : null;
}

/**
 * Scale the label's numbers to per-100 of whatever it is sold by.
 *
 * A US panel is per serving, so its figures must be divided by the serving mass
 * and multiplied by 100. Without a serving mass that conversion is impossible,
 * and returning null is the only honest answer — guessing a serving size would
 * corrupt every metric downstream by an unknown factor.
 */
export function toPer100(label: NutritionLabel): { nutrients: Nutrients; kind: 'mass' | 'volume' } | null {
  const grams = label.servingSize?.grams ?? null;
  const millilitres = label.servingSize?.milliliters ?? null;

  let factor = 1;
  let kind: 'mass' | 'volume' = 'mass';

  if (label.basis === 'per_serving') {
    const basisAmount = grams ?? millilitres;
    if (basisAmount === null || basisAmount <= 0) return null;
    factor = 100 / basisAmount;
    kind = grams !== null ? 'mass' : 'volume';
  } else {
    kind = label.basis === 'per_100ml' ? 'volume' : 'mass';
  }

  const scale = (v: number | null): number | null => (v === null ? null : round(v * factor));

  return {
    kind,
    nutrients: {
      kcal: scale(trusted(label, 'calories')),
      protein: scale(trusted(label, 'protein')),
      carbohydrate: scale(trusted(label, 'totalCarbohydrate')),
      fat: scale(trusted(label, 'totalFat')),
      fibre: scale(trusted(label, 'dietaryFiber')),
      sugar: scale(trusted(label, 'totalSugars')),
      saturatedFat: scale(trusted(label, 'saturatedFat')),
      sodium: scale(trusted(label, 'sodium')),
      other: {},
    },
  };
}

function round(v: number): number {
  return Math.round(v * 100) / 100;
}

/**
 * Build the synthetic product.
 *
 * `providerId` is 'scan' throughout, so anywhere in the app that groups or
 * labels by provider will show these as scans rather than as catalogue rows.
 * Nothing here is ever sent to an API — the id is generated locally and is
 * meaningless outside this tab.
 */
export function toProduct(
  id: string,
  label: NutritionLabel,
  entry: ScanEntry,
  now: string,
): Product {
  const source: DataOrigin = {
    provider: SCAN_PROVIDER,
    retrievedAt: now,
    derived: true,
    note: 'Read from a photo of the label.',
    // The nearest honest description: it IS the manufacturer's own panel, read
    // by us rather than supplied by the retailer.
    kind: 'retailer_label',
  };

  const per100 = toPer100(label);
  const nutrition: Product['nutrition'] = {};

  if (per100 !== null) {
    const facts: NutritionFacts = {
      basis: per100.kind === 'volume' ? 'per_100ml' : 'per_100g',
      nutrients: per100.nutrients,
      basisAmount: 100,
      source,
    };
    nutrition[facts.basis] = facts;
  }

  const quantity: Quantity | null =
    entry.packAmount !== null && entry.packAmount > 0
      ? {
          kind: entry.packUnit === 'ml' ? 'volume' : 'mass',
          netAmount: entry.packAmount,
          declared: `${entry.packAmount} ${entry.packUnit}`,
          declaredValue: entry.packAmount,
          declaredUnit: entry.packUnit,
          packCount: 1,
          servingSize: label.servingSize?.grams ?? label.servingSize?.milliliters ?? null,
          servingCount: label.servingsPerContainer,
          pieceCount: label.servingSize?.count ?? null,
          source: { ...source, note: 'Entered by you.' },
        }
      : null;

  return {
    id,
    providerId: SCAN_PROVIDER,
    externalId: id,
    name: entry.nickname?.trim() || 'Scanned item',
    brand: null,
    category: null,
    imageUrl: null,
    productUrl: null,
    gtin: null,
    store: null,
    price:
      entry.price !== null && entry.price > 0
        ? {
            amount: entry.price,
            currency: entry.currency,
            originalAmount: null,
            discountPct: null,
            inStock: null,
            observedAt: now,
            source: { ...source, note: 'Entered by you.' },
          }
        : null,
    quantity,
    nutrition,
    quality: {
      // Graded honestly rather than flatteringly: the panel is the
      // manufacturer's own, but it was read by a camera, and the reader is
      // told so beside every scanned column as well as here.
      grade:
        readableFieldCount(label).read === 4
          ? 'complete'
          : readableFieldCount(label).read >= 2
            ? 'partial'
            : readableFieldCount(label).read >= 1
              ? 'minimal'
              : 'unusable',
      completeness: readableFieldCount(label).read / readableFieldCount(label).total,
      missingFields: MACRO_FIELDS.filter((k) => trusted(label, k.key) === null).map((k) => k.label),
      warnings: label.warnings,
      stale: false,
    },
    lastUpdated: now,
  };
}

/** Rebuild the product after the user edits a value or the price. */
export function rebuild(item: ScannedItem): ScannedItem {
  return { ...item, product: toProduct(item.id, item.label, item.entry, item.scannedAt) };
}

/** Session-local id. Not a catalogue id, and never sent anywhere. */
export function newScanId(seed: number): string {
  return `scan:${seed.toString(36)}`;
}

/** How much of the panel we could actually read, for the summary line. */
export function readableFieldCount(label: NutritionLabel): { read: number; total: number } {
  const wanted = ['calories', 'protein', 'totalCarbohydrate', 'totalFat'] as const;
  const read = wanted.filter((k) => trusted(label, k) !== null).length;
  return { read, total: wanted.length };
}
